import React, { useCallback, useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import {
  StyledContainer,
  TableWrapper,
  SearchAndButtonContainer,
  SearchContainer,
  CustomSearchInput,
  InputField,
  SearchIcon,
  ButtonWrapper,
  FilterWrapper,
  StatusCell,
  ArchiveButton,
} from "./SuppressionRulesTable.styled";
import MenuButtonWrapper from "../../../../../elements/v2/MenuButtonWrapper";
import { getDensity } from "../../../../../utils/GetDensity/GetDensity";
import { generateColumnTable } from "../../../../../utils/tableUtils";
import { Icon, RoundedButton } from "../../../../../elements";
import SmartTableContainer from "../../../../../elements/v2/SmartTableContainer/SmartTableContainer";
import AddIcon from "@mui/icons-material/Add";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Switch from "@mui/material/Switch";
import Tags from "../../../../../elements/v2/Tags/Tags";
import FilterSelector from "../../../../../elements/v2/FilterSelector/FilterSelector";
import { commonFilterProps } from "../../../AsistantManagement/consts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DISPLAY_MODE } from "../../../../../components/app/consts";
import { Header } from "../../components";
import { MOCK_DATA, STATUS_ENUM, FILTER_OPTIONS } from "./consts";
import RuleItem from "../../components/RuleItem/RuleItem";

const SuppressionRulesTable = observer(() => {
  const { isMobile, isTablet, isDesktop } = useMediaQueries();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [tableData, setTableData] = useState([]);

  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const [rowSelection, setRowSelection] = useState({});
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [, setActiveRows] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoading(true);
      // Simulando una carga de 5 segundos
      await new Promise((resolve) => setTimeout(resolve, 5000));
      setTableData(MOCK_DATA);
      setIsInitialLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (hoveredRowId && rowSelection[hoveredRowId]) {
      console.log("Row is both selected and hovered:", {
        rowId: hoveredRowId,
        isSelected: true,
        isHovered: true,
      });
      setActiveRows((prev) => ({
        ...prev,
        [hoveredRowId]: true,
      }));
    } else if (hoveredRowId) {
      setActiveRows((prev) => ({
        ...prev,
        [hoveredRowId]: false,
      }));
    }
  }, [hoveredRowId, rowSelection]);

  const density = useMemo(
    () => getDensity(isMobile, isTablet, isDesktop),
    [isMobile, isTablet, isDesktop]
  );

  const [triggerFilter, setTriggerFilter] = useState([]);
  const [audienceFilter, setAudienceFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [periodFilter, setPeriodFilter] = useState([]);

  const modeTable = searchParams.get("mode") || DISPLAY_MODE.TABLE;

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleFilterChange = useCallback(
    (filterType) => (event) => {
      const newValue =
        typeof event.target.value === "string"
          ? event.target.value.split(",")
          : event.target.value;

      const filterConfigs = {
        trigger: {
          setState: setTriggerFilter,
        },
        audience: {
          setState: setAudienceFilter,
        },
        status: {
          setState: setStatusFilter,
        },
        period: {
          setState: setPeriodFilter,
        },
      };

      const config = filterConfigs[filterType];
      if (config) {
        config.setState(newValue);
      }
    },
    []
  );

  const handleClearAll = useCallback(() => {
    setTriggerFilter([]);
    setAudienceFilter([]);
    setStatusFilter([]);
    setPeriodFilter([]);
  }, []);

  const filterSelectorsConfig = useMemo(
    () => [
      {
        title: "Trigger",
        data: FILTER_OPTIONS.TRIGGER,
        minWidthForm: 103,
        minWidthPaper: 263,
        value: triggerFilter,
        filterType: "trigger",
        type: "single",
      },
      {
        title: "Audience",
        data: FILTER_OPTIONS.AUDIENCE,
        minWidthForm: 102,
        minWidthPaper: 224,
        value: audienceFilter,
        filterType: "audience",
        type: "single",
      },
      {
        title: "Status",
        data: FILTER_OPTIONS.STATUS,
        minWidthForm: 102,
        minWidthPaper: 224,
        value: statusFilter,
        filterType: "status",
        type: "single",
      },
      {
        title: "Period",
        data: FILTER_OPTIONS.PERIOD,
        minWidthForm: 158,
        minWidthPaper: 224,
        value: periodFilter,
        filterType: "period",
        type: "single",
      },
    ],
    [triggerFilter, audienceFilter, statusFilter, periodFilter]
  );

  const handleStatusToggle = useCallback((rowId) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === rowId
          ? {
              ...row,
              status:
                row.status === STATUS_ENUM.ACTIVE
                  ? STATUS_ENUM.ARCHIVED
                  : STATUS_ENUM.ACTIVE,
            }
          : row
      )
    );
  }, []);

  const getMenuItems = useCallback((row) => {
    return [
      {
        label: "Duplicate",
        onClick: () => {
          // Implementar lógica de duplicación
          console.log("Duplicate rule:", row);
        },
      },
      {
        label: "Edit",
        onClick: () => {
          // Implementar lógica de edición
          console.log("Edit rule:", row);
        },
      },
      {
        label: "Archive",
        onClick: () => {
          // Implementar lógica de archivo
          console.log("Archive rule:", row);
        },
      },
    ];
  }, []);

  const handleRowMouseEnter = useCallback((rowId) => {
    setHoveredRowId(rowId);
  }, []);

  const handleRowMouseLeave = useCallback(() => {
    setHoveredRowId(null);
  }, []);

  const columns = [
    {
      ...generateColumnTable("name", "Rule Name"),
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      ...generateColumnTable("trigger", "Trigger"),
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      ...generateColumnTable("contacts", "Contacts"),
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      ...generateColumnTable("status", "Status"),
      Cell: ({ cell, row }) => {
        const status = cell.getValue();
        const isActive = status === STATUS_ENUM.ACTIVE;
        return (
          <StatusCell onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={isActive}
              onChange={() => handleStatusToggle(row.original.id)}
              size="small"
            />
          </StatusCell>
        );
      },
    },
    {
      ...generateColumnTable("period", "Period"),
      size: 100,
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      ...generateColumnTable("channels", "Channels"),
      size: 250,
      Cell: ({ cell }) => {
        const channels = cell.getValue();
        return channels && channels.length > 0 ? (
          <Tags names={channels} scrollable={true} />
        ) : (
          "-"
        );
      },
    },
    {
      ...generateColumnTable("actions", "Actions"),
      Cell: ({ row }) => {
        return <MenuButtonWrapper items={getMenuItems(row.original)} />;
      },
    },
  ];

  const handleModeTableChange = useCallback(
    (mode) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("mode", mode);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleRowSelectionChange = useCallback((newSelection) => {
    setRowSelection(newSelection);
  }, []);

  const selectedCount = Object.keys(rowSelection).length;

  const handleArchiveSelected = useCallback(() => {
    const selectedIds = Object.keys(rowSelection);
    setTableData((prevData) =>
      prevData.map((row) =>
        selectedIds.includes(row.id)
          ? { ...row, status: STATUS_ENUM.ARCHIVED }
          : row
      )
    );
    setRowSelection({});
  }, [rowSelection]);

  return (
    <>
      <Header
        title="Supression rules"
        description="Prevent over-communication by setting limits on when contacts are reached."
      />

      <StyledContainer>
        <SearchAndButtonContainer>
          <SearchContainer>
            <CustomSearchInput>
              <SearchIcon>
                <Icon name="magnifier" height="20px" width="20px" />
              </SearchIcon>
              <InputField
                type="text"
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search rules by name or keyword"
              />
            </CustomSearchInput>
            <FilterWrapper>
              {filterSelectorsConfig.map((config) => (
                <FilterSelector
                  key={config.title}
                  {...commonFilterProps()}
                  {...config}
                  handleChange={handleFilterChange(config.filterType)}
                />
              ))}
              <RoundedButton
                width="100px"
                height="40px"
                kind="confirm"
                outline={true}
                fontWeight="normal"
                fontSize="14px"
                margin="0"
                borderRadius="8px"
                onClick={handleClearAll}
              >
                Clear Filters
              </RoundedButton>
              {selectedCount > 0 && (
                <ArchiveButton kind="default" onClick={handleArchiveSelected}>
                  <Inventory2OutlinedIcon />
                  Archive Selection ({selectedCount})
                </ArchiveButton>
              )}
            </FilterWrapper>
          </SearchContainer>
          <ButtonWrapper>
            <SmartTableContainer.ModeSwitcher
              mode={modeTable}
              setMode={handleModeTableChange}
            />
            <RoundedButton
              kind="blue"
              onClick={() => navigate('/v2/suppression-rules/create')}
              height="40px"
              style={{ paddingRight: "16px" }}
            >
              <AddIcon />
              Create
            </RoundedButton>
          </ButtonWrapper>
        </SearchAndButtonContainer>

        <TableWrapper
          style={{
            height: "360px",
            maxHeight: "calc(100vh - 280px)",
            overflow: "auto",
            position: "relative"
          }}
        >
          <SmartTableContainer
            columns={columns}
            data={tableData}
            rowCount={tableData.length}
            pagination={pagination}
            setPagination={setPagination}
            itemToRender={<RuleItem />}
            sorting={sorting}
            setSorting={setSorting}
            isLoading={isInitialLoading}
            enableRowSelection={true}
            density={density}
            mode={modeTable}
            columnsGrid={3}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            onRowMouseEnter={handleRowMouseEnter}
            onRowMouseLeave={handleRowMouseLeave}
            showEmptyData={
              !isInitialLoading &&
              tableData.length === 0 && (
                <SmartTableContainer.EmptyState
                  title="No rules found"
                  description="You haven't created any suppression rules yet. To manage your communication limits, please create a rule first."
                />
              )
            }
          />
        </TableWrapper>
      </StyledContainer>
    </>
  );
});

export default SuppressionRulesTable;
